/**
 * FinOpsPage (NC-867 T6) — per-cost-center cost overview.
 *
 * Shows, per active cost-center: monthly budget, Ist (actual, from cost-daily)
 * and Schätzung (estimate, from active-run cost estimation), plus a bar chart
 * and a CSV export for chargeback. In Phase 0 there are no actuals yet, so Ist
 * is 0 and the figures are Schätzung-driven — surfaced with an info banner.
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Download as DownloadIcon, Euro as EuroIcon } from '@mui/icons-material';
import { costOverviewApi, CostOverview } from '../services/costOverviewService';
import { formatDateSafe } from '../utils/dateFormat';

const eur = (n: number): string => `${n.toFixed(2)} €`;

export default function FinOpsPage() {
  const [data, setData] = useState<CostOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    costOverviewApi
      .get()
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Kostenübersicht konnte nicht geladen werden.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Memoized so the downstream useMemo hooks (totals, chartData) have a stable
  // dependency (react-hooks/exhaustive-deps).
  const rows = useMemo(() => data?.costCenters ?? [], [data]);

  const totals = useMemo(
    () =>
      rows.reduce(
        (acc, r) => ({
          budget: acc.budget + r.budgetMonthlyEur,
          ist: acc.ist + r.istEur,
          schaetzung: acc.schaetzung + r.schaetzungEur,
        }),
        { budget: 0, ist: 0, schaetzung: 0 }
      ),
    [rows]
  );

  const chartData = useMemo(
    () =>
      rows.map((r) => ({
        name: r.code,
        Budget: r.budgetMonthlyEur,
        Ist: r.istEur,
        'Schätzung': r.schaetzungEur,
      })),
    [rows]
  );

  const handleCsvExport = () => {
    const header = ['Kostenstelle', 'Name', 'Typ', 'Budget (EUR)', 'Ist (EUR)', 'Schätzung (EUR)'];
    const body = rows.map((r) => [
      r.code,
      r.displayName,
      r.type,
      r.budgetMonthlyEur.toFixed(2),
      r.istEur.toFixed(2),
      r.schaetzungEur.toFixed(2),
    ]);
    const csv = [header, ...body].map((line) => line.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `finops-${data?.month ?? 'export'}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Box>
      <Box display="flex" alignItems="center" gap={1} mb={2}>
        <EuroIcon color="primary" />
        <Typography variant="h4">FinOps — Kosten je Kostenstelle</Typography>
      </Box>

      {loading ? (
        <Box>
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} data-testid="finops-skeleton-line" height={48} sx={{ mb: 0.5 }} />
          ))}
        </Box>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : rows.length === 0 ? (
        <Alert severity="info">
          Keine aktiven Kostenstellen vorhanden. Lege unter „Kostenstellen" welche an, damit hier
          Kosten je Kostenstelle erscheinen.
        </Alert>
      ) : (
        <>
          <Alert severity="info" sx={{ mb: 2 }}>
            Monat <strong>{data?.month}</strong>. Ist-Kosten stammen aus den täglichen Azure-Rollups
            (noch leer, bis der Kosten-Export live ist) — die Werte sind derzeit überwiegend{' '}
            <strong>Schätzung</strong> aus aktiven Läufen.
          </Alert>

          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">Budget vs. Ist vs. Schätzung</Typography>
                <Button
                  size="small"
                  startIcon={<DownloadIcon />}
                  onClick={handleCsvExport}
                  data-testid="finops-csv-export"
                >
                  Als CSV exportieren
                </Button>
              </Box>
              <Box sx={{ width: '100%', height: 320 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="Budget" name="Budget" fill="#90caf9" />
                    <Bar dataKey="Ist" name="Ist" fill="#1976d2" />
                    <Bar dataKey="Schätzung" name="Schätzung" fill="#ffb74d" />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Kostenstelle</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Typ</TableCell>
                      <TableCell align="right">Budget</TableCell>
                      <TableCell align="right">Ist</TableCell>
                      <TableCell align="right">Schätzung</TableCell>
                      <TableCell align="right">Budget-Auslastung</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((r) => {
                      const reference = r.istEur > 0 ? r.istEur : r.schaetzungEur;
                      const usage = r.budgetMonthlyEur > 0 ? reference / r.budgetMonthlyEur : 0;
                      const over = r.budgetMonthlyEur > 0 && reference > r.budgetMonthlyEur;
                      return (
                        <TableRow key={r.code} hover>
                          <TableCell sx={{ fontFamily: 'monospace' }}>{r.code}</TableCell>
                          <TableCell>{r.displayName}</TableCell>
                          <TableCell>{r.type}</TableCell>
                          <TableCell align="right">{r.budgetMonthlyEur > 0 ? eur(r.budgetMonthlyEur) : '—'}</TableCell>
                          <TableCell align="right">{eur(r.istEur)}</TableCell>
                          <TableCell align="right">{eur(r.schaetzungEur)}</TableCell>
                          <TableCell align="right">
                            {r.budgetMonthlyEur > 0 ? (
                              <Chip
                                size="small"
                                label={`${Math.round(usage * 100)}%`}
                                color={over ? 'error' : usage > 0.8 ? 'warning' : 'success'}
                                variant={over ? 'filled' : 'outlined'}
                              />
                            ) : (
                              '—'
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>

              <Box
                sx={{
                  mt: 2,
                  p: 1.5,
                  bgcolor: 'primary.50',
                  borderRadius: 1,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Summe ({rows.length} Kostenstellen)
                </Typography>
                <Typography variant="body2">
                  Budget {eur(totals.budget)} · Ist {eur(totals.ist)} · Schätzung {eur(totals.schaetzung)}
                </Typography>
              </Box>

              {data?.generatedAt && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Stand: {formatDateSafe(new Date(data.generatedAt), 'yyyy-MM-dd HH:mm')} · Ist aus
                  cost-daily, Schätzung aus aktiven Läufen (Azure Retail Prices).
                </Typography>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </Box>
  );
}
